import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertVendorSchema, type Vendor } from "db/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "../../supabaseClient";

type VendorFormProps = {
  onSuccess: () => void;
  initialData?: Vendor | null;
};

export default function VendorForm({
  onSuccess,
  initialData,
}: VendorFormProps) {
  const { toast } = useToast();
  const [agencies, setAgencies] = useState<{ id: string; name: string }[]>([]);

  // Fetch agency names
  useEffect(() => {
    async function fetchAgencies() {
      const { data, error } = await supabase
        .from("agency")
        .select("id, name");

      if (error) {
        console.error("Error fetching agencies:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to fetch agency data",
        });
      } else {
        setAgencies(data || []);
      }
    }

    fetchAgencies();
  }, [toast]);

  const form = useForm({
    resolver: zodResolver(insertVendorSchema),
    defaultValues: initialData || {
      name: "",
      email: "",
      phone: "",
      websiteURL: "",
      industry: "",
      address: "",
      status: "active",
      requiredSkills: "",
      contractStartDate: "",
      contractEndDate: "",
      commissionPercentage: "",
      relationshipManager: "",
      agency_id: "", 
    },
  });

  const onSubmit = async (values: any) => {
    try {
      const transformedValues = {
        ...values,
        status: values.status,
        agency_id: values.agency_id, // Include agency_id in payload
      };

      const tableName = "vendors";
      const { data, error } = initialData
        ? await supabase
            .from(tableName)
            .update(transformedValues)
            .eq("id", initialData.id)
            .select()
        : await supabase
            .from(tableName)
            .insert([transformedValues])
            .select();

      if (error) {
        console.error("Supabase error:", error);
        throw new Error(error.message || "Failed to save vendor");
      }

      toast({
        title: "Success",
        description: initialData
          ? "Vendor updated successfully"
          : "Vendor created successfully",
      });

      onSuccess();
    } catch (error: any) {
      console.error("Vendor form error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to save vendor",
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
    

        <FormField
          control={form.control}
          name="agency_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Agency</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select agency" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {agencies.map((agency) => (
                    <SelectItem key={agency.id} value={agency.id}>
                      {agency.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input {...field} id="name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="email" {...field} id="email" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone</FormLabel>
              <FormControl>
                <Input type="tel" {...field} id="phone" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="websiteURL"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Website URL</FormLabel>
              <FormControl>
                <Input type="text" {...field} id="websiteURL" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="industry"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Industry</FormLabel>
              <FormControl>
                <Input type="text" {...field} id="industry" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="address"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Address</FormLabel>
              <FormControl>
                <Input type="text" {...field} id="address" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="requiredSkills"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Required Skills</FormLabel>
              <FormControl>
                <Input type="text" {...field} id="requiredSkills" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="contractStartDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Contract Start Date</FormLabel>
              <FormControl>
                <Input type="date" {...field} id="contractStartDate" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="contractEndDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Contract End Date</FormLabel>
              <FormControl>
                <Input type="date" {...field} id="contractEndDate" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="commissionPercentage"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Commission Percentage</FormLabel>
              <FormControl>
                <Input type="number" {...field} id="commissionPercentage" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="relationshipManager"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Relationship Manager</FormLabel>
              <FormControl>
                <Input type="text" {...field} id="relationshipManager" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full">
          {initialData ? "Update Vendor" : "Create Vendor"}
        </Button>
      </form>
    </Form>
  );
}
